import React, { useEffect, useMemo, useState } from "react";
import * as Yup from "yup";
import { Formik, Form } from "formik";
import { toast } from "react-toastify";
import { i18n } from "../../translate/i18n";
import AppModal from "../AppModal";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import ChatbotModal from "../ChatbotModal";

const QueueSchema = Yup.object().shape({
	name: Yup.string()
		.min(2, "Too Short!")
		.max(50, "Too Long!")
		.required("Required"),
	color: Yup.string().min(3, "Too Short!").max(9, "Too Long!").required(),
	greetingMessage: Yup.string(),
	startWork: Yup.string(),
	endWork: Yup.string(),
	startLunch: Yup.string(),
	endLunch: Yup.string(),
	absenceMessage: Yup.string(),
	messageLunch: Yup.string(),
});

const initialState = {
	name: "",
	color: "#3b82f6",
	greetingMessage: "",
	startWork: "",
	endWork: "",
	absenceMessage: "",
	dayWork: "",
	daySchedules: null,
	startLunch: "",
	endLunch: "",
	messageLunch: "",
	chatbotId: "",
	startWeekend: "",
	endWeekend: "",
};

const SCHEDULE_DAYS = [
	{ key: "segunda", label: "Segunda", dayWorkValue: "segunda" },
	{ key: "terca", label: "Terça", dayWorkValue: "terça" },
	{ key: "quarta", label: "Quarta", dayWorkValue: "quarta" },
	{ key: "quinta", label: "Quinta", dayWorkValue: "quinta" },
	{ key: "sexta", label: "Sexta", dayWorkValue: "sexta" },
	{ key: "sabado", label: "Sábado", dayWorkValue: "sábado" },
	{ key: "domingo", label: "Domingo", dayWorkValue: "domingo" },
];

const DEFAULT_DAYS = [
	"domingo",
	"segunda",
	"terça",
	"quarta",
	"quinta",
	"sexta",
	"sábado",
];

const daysOfWeek = SCHEDULE_DAYS.map((day, index) => ({
	id: index + 1,
	name: day.label,
	value: day.dayWorkValue,
}));

function getDefaultDays() {
	return [...DEFAULT_DAYS];
}

function defaultDaySchedules() {
	return {
		segunda: { enabled: true, start: "09:00", end: "18:00" },
		terca: { enabled: true, start: "09:00", end: "18:00" },
		quarta: { enabled: true, start: "09:00", end: "18:00" },
		quinta: { enabled: true, start: "09:00", end: "18:00" },
		sexta: { enabled: true, start: "09:00", end: "18:00" },
		sabado: { enabled: false, start: "09:00", end: "13:00" },
		domingo: { enabled: false, start: "09:00", end: "13:00" },
	};
}

function normalizeDayKey(value) {
	return String(value || "")
		.toLowerCase()
		.normalize("NFD")
		.replace(/[\u0300-\u036f]/g, "")
		.trim();
}

function normalizeClock(value, fallback = "09:00") {
	const raw = String(value || "").trim();
	if (!raw) return fallback;
	const match = raw.match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/);
	if (!match) return raw.slice(0, 5) || fallback;
	const hh = String(Math.min(23, Math.max(0, Number(match[1])))).padStart(2, "0");
	const mm = String(Math.min(59, Math.max(0, Number(match[2])))).padStart(2, "0");
	return `${hh}:${mm}`;
}

/** Prod usa longtext: daySchedules pode vir como string JSON. */
function parseDaySchedulesRaw(raw) {
	if (raw == null) return null;
	if (typeof raw === "string") {
		const trimmed = raw.trim();
		if (!trimmed) return null;
		try {
			return parseDaySchedulesRaw(JSON.parse(trimmed));
		} catch {
			return null;
		}
	}
	if (typeof raw === "object" && !Array.isArray(raw)) return raw;
	return null;
}

function buildDaySchedulesFromQueue(data) {
	const base = defaultDaySchedules();
	const raw = parseDaySchedulesRaw(data?.daySchedules);
	if (raw) {
		Object.keys(base).forEach((key) => {
			const item = raw[key] || raw[normalizeDayKey(key)];
			if (!item) return;
			base[key] = {
				enabled: Boolean(item.enabled),
				start: normalizeClock(item.start, base[key].start),
				end: normalizeClock(item.end, base[key].end),
			};
		});
		return base;
	}

	const enabledDays = new Set(
		normalizeDayWork(data?.dayWork).map((d) => normalizeDayKey(d))
	);
	const start = normalizeClock(data?.startWork, "09:00");
	const end = normalizeClock(data?.endWork, "18:00");
	const weekendStart = normalizeClock(data?.startWeekend, start);
	const weekendEnd = normalizeClock(data?.endWeekend, end);

	Object.keys(base).forEach((key) => {
		const isWeekend = key === "sabado" || key === "domingo";
		base[key] = {
			enabled: enabledDays.size === 0 ? !isWeekend : enabledDays.has(key),
			start: isWeekend ? weekendStart : start,
			end: isWeekend ? weekendEnd : end,
		};
	});
	return base;
}

function schedulesToLegacy(daySchedules) {
	const enabled = SCHEDULE_DAYS.filter((d) => daySchedules?.[d.key]?.enabled).map(
		(d) => d.dayWorkValue
	);
	const weekday = SCHEDULE_DAYS.find(
		(d) => !["sabado", "domingo"].includes(d.key) && daySchedules?.[d.key]?.enabled
	);
	const weekend = SCHEDULE_DAYS.find(
		(d) => ["sabado", "domingo"].includes(d.key) && daySchedules?.[d.key]?.enabled
	);
	return {
		dayWork: enabled,
		startWork: normalizeClock(
			weekday ? daySchedules[weekday.key].start : daySchedules?.segunda?.start,
			"09:00"
		),
		endWork: normalizeClock(
			weekday ? daySchedules[weekday.key].end : daySchedules?.segunda?.end,
			"18:00"
		),
		startWeekend: weekend
			? normalizeClock(daySchedules[weekend.key].start, "")
			: normalizeClock(daySchedules?.sabado?.start, ""),
		endWeekend: weekend
			? normalizeClock(daySchedules[weekend.key].end, "")
			: normalizeClock(daySchedules?.sabado?.end, ""),
	};
}

function normalizeDaySchedulesForSave(daySchedules) {
	const out = {};
	SCHEDULE_DAYS.forEach((day) => {
		const item = daySchedules?.[day.key] || {};
		out[day.key] = {
			enabled: Boolean(item.enabled),
			start: normalizeClock(item.start, "09:00"),
			end: normalizeClock(item.end, "18:00"),
		};
	});
	return out;
}

function SectionCard({ title, description, children }) {
	return (
		<section className="rounded-2xl border border-default-200 bg-default-50 p-4 dark:border-default-700 dark:bg-default-900">
			<div className="mb-4">
				<h3 className="m-0 text-sm font-semibold text-default-900 dark:text-default-100">
					{title}
				</h3>
				{description && (
					<p className="mt-1 mb-0 text-sm text-default-500 dark:text-default-400">
						{description}
					</p>
				)}
			</div>
			<div className="space-y-4">{children}</div>
		</section>
	);
}

function FieldLabel({ children }) {
	return (
		<label className="mb-2 block text-sm font-medium text-default-800 dark:text-default-100">
			{children}
		</label>
	);
}

function FieldError({ message }) {
	if (!message) return null;
	return <p className="mt-1 mb-0 text-xs text-danger">{message}</p>;
}

function IconSparkles() {
	return (
		<svg className="size-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
			<path d="m12 3 1.9 5.1L19 10l-5.1 1.9L12 17l-1.9-5.1L5 10l5.1-1.9L12 3Z" />
			<path d="M5 3v4" />
			<path d="M3 5h4" />
			<path d="M19 17v4" />
			<path d="M17 19h4" />
		</svg>
	);
}

function IconEdit() {
	return (
		<svg className="size-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
			<path d="M12 20h9" />
			<path d="M16.5 3.5a2.121 2.121 0 1 1 3 3L7 19l-4 1 1-4Z" />
		</svg>
	);
}

function IconTrash() {
	return (
		<svg className="size-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
			<polyline points="3 6 5 6 21 6" />
			<path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
		</svg>
	);
}

function normalizeDayWork(dayWork) {
	if (Array.isArray(dayWork)) {
		return dayWork.map(item => String(item).toLowerCase());
	}

	if (typeof dayWork === "string" && dayWork.trim()) {
		try {
			const parsed = JSON.parse(dayWork);
			if (Array.isArray(parsed)) {
				return parsed.map(item => String(item).toLowerCase());
			}
		} catch {
			return dayWork
				.split(",")
				.map(item => item.trim().toLowerCase())
				.filter(Boolean);
		}
	}

	return getDefaultDays();
}

function QueueModal({ open, onClose, queueId }) {
	const [queue, setQueue] = useState(initialState);
	const [chatBotModalOpen, setChatBotModalOpen] = useState(false);
	const [selectedChatbot, setSelectedChatbot] = useState(null);
	const [chatbots, setChatbots] = useState([]);
	const [selectedDaysWork, setSelectedDaysWork] = useState(getDefaultDays);
	const [daySchedules, setDaySchedules] = useState(defaultDaySchedules);

	const selectedChatbotData = useMemo(
		() => chatbots.find(chatbot => String(chatbot.id) === String(queue.chatbotId)) || null,
		[chatbots, queue.chatbotId]
	);

	useEffect(() => {
		if (!open) return undefined;

		let cancelled = false;

		(async () => {
			try {
				const { data } = await api.get("/chatbot");
				if (!cancelled) {
					setChatbots(Array.isArray(data) ? data : []);
				}
			} catch (err) {
				if (!cancelled) {
					toastError(err);
				}
			}
		})();

		return () => {
			cancelled = true;
		};
	}, [open, chatBotModalOpen]);

	useEffect(() => {
		if (!open) return undefined;

		let cancelled = false;

		(async () => {
			if (!queueId) {
				setQueue(initialState);
				setSelectedDaysWork(getDefaultDays());
				setDaySchedules(defaultDaySchedules());
				return;
			}

			try {
				const { data } = await api.get(`/queue/${queueId}`);
				if (cancelled) return;

				setQueue(prevState => ({
					...prevState,
					...data,
					color: data.color || "#3b82f6",
					chatbotId: data.chatbotId || null,
				}));
				const schedules = buildDaySchedulesFromQueue(data);
				setDaySchedules(schedules);
				setSelectedDaysWork(
					SCHEDULE_DAYS.filter((d) => schedules[d.key]?.enabled).map((d) => d.dayWorkValue)
				);
			} catch (err) {
				if (!cancelled) {
					toastError(err);
				}
			}
		})();

		return () => {
			cancelled = true;
			setQueue(initialState);
			setSelectedDaysWork(getDefaultDays());
			setDaySchedules(defaultDaySchedules());
		};
	}, [queueId, open]);

	const addChatbotQueue = async chatbotId => {
		const targetQueueId = queue.id || queueId;

		if (!targetQueueId) {
			setQueue(prevState => ({ ...prevState, chatbotId: chatbotId || null }));
			toast.info(i18n.t("queueModal.notification.saveBeforeChatbot"));
			return;
		}

		try {
			await api.put(`/queue/${targetQueueId}/chatbot`, {
				chatbotId,
			});
			toast.success(`${i18n.t("queueModal.notification.chatbotAdded")}`);
			setQueue(prevState => ({
				...prevState,
				chatbotId: chatbotId || "",
			}));
		} catch (err) {
			toastError(err);
		}
	};

	const createChatbot = async () => {
		try {
			const { data } = await api.post("/chatbot", {
				name: `Chatbot ${chatbots.length + 1}`,
			});
			setChatbots(prevState => [...prevState, data]);
			setSelectedChatbot(data);
			setChatBotModalOpen(true);
		} catch (err) {
			toastError(err);
		}
	};

	const handleClose = () => {
		onClose();
		setQueue(initialState);
		setSelectedDaysWork(getDefaultDays());
		setDaySchedules(defaultDaySchedules());
		setSelectedChatbot(null);
	};

	const deleteChatbot = async (chatbotId, onDeleted) => {
		try {
			await api.delete(`/chatbot/${chatbotId}`);
			setChatbots(prevState => prevState.filter(chatbot => chatbot.id !== chatbotId));
			if (typeof onDeleted === "function") {
				await onDeleted();
			}
		} catch (err) {
			toastError(err);
		}
	};

	const handleToggleDaySchedule = (key) => {
		setDaySchedules((current) => {
			const next = {
				...current,
				[key]: {
					...current[key],
					enabled: !current[key]?.enabled,
				},
			};
			setSelectedDaysWork(
				SCHEDULE_DAYS.filter((d) => next[d.key]?.enabled).map((d) => d.dayWorkValue)
			);
			return next;
		});
	};

	const handleDayScheduleTime = (key, field, value) => {
		const clock = normalizeClock(value, field === "end" ? "18:00" : "09:00");
		setDaySchedules((current) => ({
			...current,
			[key]: {
				...current[key],
				enabled: Boolean(current[key]?.enabled),
				[field]: clock,
			},
		}));
	};

	const handleSaveQueue = async values => {
		try {
			const schedules = normalizeDaySchedulesForSave(daySchedules);
			const legacy = schedulesToLegacy(schedules);
			const payload = {
				name: values.name,
				color: values.color,
				greetingMessage: values.greetingMessage || "",
				absenceMessage: values.absenceMessage || "",
				startLunch: normalizeClock(values.startLunch, ""),
				endLunch: normalizeClock(values.endLunch, ""),
				messageLunch: values.messageLunch || "",
				...legacy,
				daySchedules: schedules,
				chatbotId: values.chatbotId ? Number(values.chatbotId) : null,
			};

			if (queueId) {
				await api.put(`/queue/${queueId}`, payload);
			} else {
				await api.post("/queue", payload);
			}

			toast.success(`${i18n.t("queueModal.notification.title")}`);
			handleClose();
		} catch (err) {
			toastError(err);
		}
	};

	return (
		<>
			<ChatbotModal
				open={chatBotModalOpen}
				onClose={() => {
					setChatBotModalOpen(false);
				}}
				selectedChatbot={selectedChatbot}
				setSelectedChatbot={setSelectedChatbot}
				chatbots={chatbots}
				setChatbots={setChatbots}
			/>

			<Formik
				initialValues={queue}
				enableReinitialize
				validationSchema={QueueSchema}
				onSubmit={async (values, actions) => {
					await handleSaveQueue(values);
					actions.setSubmitting(false);
				}}
			>
				{({
					touched,
					errors,
					isSubmitting,
					values,
					handleChange,
					handleBlur,
					setFieldValue,
					submitForm,
				}) => (
					<AppModal
						open={open && !chatBotModalOpen}
						onClose={handleClose}
						title={
							queueId
								? `${i18n.t("queueModal.title.edit")}`
								: `${i18n.t("queueModal.title.add")}`
						}
						subtitle="Defina identidade, horários, mensagens e automações do setor"
						maxWidth={900}
						footer={
							<>
								<button
									type="button"
									className="btn btn-outline-secondary"
									onClick={handleClose}
									disabled={isSubmitting}
								>
									{i18n.t("queueModal.buttons.cancel")}
								</button>
								<button
									type="button"
									className="btn bg-primary text-white"
									onClick={submitForm}
									disabled={isSubmitting}
								>
									{isSubmitting && (
										<span className="inline-block size-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />
									)}
									{queueId
										? `${i18n.t("queueModal.buttons.okEdit")}`
										: `${i18n.t("queueModal.buttons.okAdd")}`}
								</button>
							</>
						}
					>
						<Form className="space-y-6">
							<SectionCard
								title="Dados do setor"
								description="Nome, cor de identificação e dias de atendimento."
							>
								<div className="grid grid-cols-1 gap-4 md:grid-cols-2">
									<div>
										<FieldLabel>{i18n.t("queueModal.form.name")}</FieldLabel>
										<input
											type="text"
											name="name"
											value={values.name || ""}
											onChange={handleChange}
											onBlur={handleBlur}
											className="form-input w-full"
											placeholder="Ex.: Suporte, Financeiro..."
											autoFocus
										/>
										<FieldError message={touched.name && errors.name} />
									</div>

									<div>
										<FieldLabel>{i18n.t("queueModal.form.color")}</FieldLabel>
										<div className="form-input flex w-full items-center gap-3 px-2 py-1">
											<input
												type="color"
												value={values.color || "#3b82f6"}
												onChange={event => setFieldValue("color", event.target.value)}
												className="h-9 w-11 cursor-pointer rounded border-0 bg-transparent p-0"
											/>
											<input
												type="text"
												name="color"
												value={values.color || ""}
												onChange={handleChange}
												onBlur={handleBlur}
												className="min-w-0 flex-1 bg-transparent text-sm text-default-800 outline-none dark:text-default-100"
												placeholder="#3b82f6"
											/>
										</div>
										<FieldError message={touched.color && errors.color} />
									</div>
								</div>

								<div>
									<FieldLabel>Expediente por dia</FieldLabel>
									<p className="mb-3 mt-0 text-xs text-default-500 dark:text-default-400">
										Ative o dia e defina horário de início e fim. A IA só atende e agenda dentro desses horários.
									</p>
									<div className="space-y-2">
										{SCHEDULE_DAYS.map((day) => {
											const schedule = daySchedules[day.key] || {
												enabled: false,
												start: "09:00",
												end: "18:00",
											};
											return (
												<div
													key={day.key}
													className={`grid grid-cols-1 items-center gap-2 rounded-xl border px-3 py-2.5 md:grid-cols-[8rem_1fr_1fr] ${
														schedule.enabled
															? "border-primary/30 bg-primary/5"
															: "border-default-200 bg-white dark:border-default-700 dark:bg-default-950"
													}`}
												>
													<label className="flex cursor-pointer items-center gap-2 text-sm font-medium text-default-800 dark:text-default-100">
														<input
															type="checkbox"
															className="size-4 rounded border-default-300 text-primary focus:ring-primary"
															checked={Boolean(schedule.enabled)}
															onChange={() => handleToggleDaySchedule(day.key)}
														/>
														{day.label}
													</label>
													<label className="flex flex-col gap-1 text-xs text-default-600 dark:text-default-300">
														Início
														<input
															type="time"
															className="form-input"
															step={600}
															disabled={!schedule.enabled}
															value={schedule.start || ""}
															onChange={(e) =>
																handleDayScheduleTime(day.key, "start", e.target.value)
															}
														/>
													</label>
													<label className="flex flex-col gap-1 text-xs text-default-600 dark:text-default-300">
														Fim
														<input
															type="time"
															className="form-input"
															step={600}
															disabled={!schedule.enabled}
															value={schedule.end || ""}
															onChange={(e) =>
																handleDayScheduleTime(day.key, "end", e.target.value)
															}
														/>
													</label>
												</div>
											);
										})}
									</div>
								</div>
							</SectionCard>

							<SectionCard
								title="Almoço e ausência"
								description="Pausa de almoço (bloqueia a IA) e mensagem fora do expediente."
							>
								<div className="grid grid-cols-1 gap-4 md:grid-cols-2">
									<div>
										<FieldLabel>Início almoço</FieldLabel>
										<input
											type="time"
											name="startLunch"
											value={values.startLunch || ""}
											onChange={handleChange}
											onBlur={handleBlur}
											step={600}
											className="form-input w-full"
										/>
									</div>

									<div>
										<FieldLabel>Fim almoço</FieldLabel>
										<input
											type="time"
											name="endLunch"
											value={values.endLunch || ""}
											onChange={handleChange}
											onBlur={handleBlur}
											step={600}
											className="form-input w-full"
										/>
									</div>
								</div>
							</SectionCard>

							<SectionCard
								title="Mensagens automáticas"
								description="Textos usados na saudação, ausência e pausa de almoço."
							>
								<div>
									<FieldLabel>{i18n.t("queueModal.form.greetingMessage")}</FieldLabel>
									<textarea
										name="greetingMessage"
										value={values.greetingMessage || ""}
										onChange={handleChange}
										onBlur={handleBlur}
										className="form-input min-h-[110px] w-full resize-y"
										placeholder="Mensagem inicial do setor"
									/>
									<FieldError
										message={touched.greetingMessage && errors.greetingMessage}
									/>
								</div>

								<div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
									<div>
										<FieldLabel>{i18n.t("queueModal.form.absenceMessage")}</FieldLabel>
										<textarea
											name="absenceMessage"
											value={values.absenceMessage || ""}
											onChange={handleChange}
											onBlur={handleBlur}
											className="form-input min-h-[96px] w-full resize-y"
											placeholder="Mensagem fora do horário"
										/>
										<FieldError
											message={touched.absenceMessage && errors.absenceMessage}
										/>
									</div>

									<div>
										<FieldLabel>Mensagem almoço</FieldLabel>
										<textarea
											name="messageLunch"
											value={values.messageLunch || ""}
											onChange={handleChange}
											onBlur={handleBlur}
											className="form-input min-h-[96px] w-full resize-y"
											placeholder="Mensagem exibida durante o almoço"
										/>
									</div>
								</div>
							</SectionCard>

							<SectionCard
								title="Chatbot"
								description="Vincule ou gerencie o chatbot responsável por este setor."
							>
								<div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1fr)_auto]">
									<div>
										<FieldLabel>Chatbot vinculado</FieldLabel>
										<select
											className="form-select w-full"
											value={values.chatbotId || null}
											onChange={async event => {
												const chatbotId = event.target.value || null;
												setFieldValue("chatbotId", chatbotId);
												setQueue(prevState => ({
													...prevState,
													chatbotId,
												}));
												if (queueId || queue.id) {
													await addChatbotQueue(chatbotId ? Number(chatbotId) : null);
												}
											}}
										>
											<option value="">Nenhum chatbot</option>
											{chatbots.map(chatbot => (
												<option key={chatbot.id} value={chatbot.id}>
													{chatbot.name}
												</option>
											))}
										</select>
										<p className="mt-2 mb-0 text-xs text-default-500 dark:text-default-400">
											{queueId || queue.id
												? "A troca do chatbot é aplicada imediatamente ao setor."
												: "Para vincular um chatbot, salve o setor primeiro."}
										</p>
									</div>

									<div className="flex flex-wrap items-end gap-2">
										<button
											type="button"
											className="btn btn-outline-secondary"
											onClick={createChatbot}
										>
											<IconSparkles />
											Adicionar chatbot
										</button>

										{selectedChatbotData && (
											<>
												<button
													type="button"
													className="btn btn-outline-secondary"
													onClick={() => {
														setSelectedChatbot(selectedChatbotData);
														setChatBotModalOpen(true);
													}}
												>
													<IconEdit />
													Editar
												</button>
												<button
													type="button"
													className="btn border border-danger/20 bg-danger/10 text-danger hover:bg-danger/15"
													onClick={async () => {
														await deleteChatbot(selectedChatbotData.id, async () => {
															setFieldValue("chatbotId", null);
															setQueue(prevState => ({
																...prevState,
																chatbotId: null,
															}));
															if (queueId || queue.id) {
																await addChatbotQueue(null);
															}
														});
													}}
												>
													<IconTrash />
													Excluir
												</button>
											</>
										)}
									</div>
								</div>
							</SectionCard>
						</Form>
					</AppModal>
				)}
			</Formik>
		</>
	);
}

export default QueueModal;
